import React from 'react';
import { Card, Form, Input, InputNumber, DatePicker, Select, Row, Col, Space, Spin } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { FormInstance } from 'antd/es/form';

const { Option } = Select;

interface GeneralInformationProps {
  form: FormInstance;
  discriminationBasis?: string;
  caseNo?: number | null;
  loadingCaseNo?: boolean;
}

const GeneralInformation: React.FC<GeneralInformationProps> = ({ form, discriminationBasis, caseNo, loadingCaseNo = false }) => {
  // Get the basis-specific field label
  const getYourBasisLabel = () => {
    switch (discriminationBasis) {
      case 'Age':
        return 'Your Age is:';
      case 'Color':
        return 'Your Color is:';
      case 'Mental/Physical Handicap':
        return 'Your Mental/Physical Handicap is:';
      case 'National Origin':
        return 'Your National origin is:';
      case 'Race':
        return 'Your Race is:';
      case 'Religion':
        return 'Your Religion is:';
      case 'Reprisal':
        return 'Your Reprisal is:';
      case 'Sex':
        return 'Your Sex is:';
      default:
        return 'Your basis is:';
    }
  };

  const getAccusedBasisLabel = () => {
    switch (discriminationBasis) {
      case 'Age':
        return "Accused's Age is:";
      case 'Color':
        return "Accused's Color is:";
      case 'Mental/Physical Handicap':
        return "Accused's Mental/Physical Handicap is:";
      case 'National Origin':
        return "Accused's National origin is:";
      case 'Race':
        return "Accused's Race is:";
      case 'Religion':
        return "Accused's Religion is:";
      case 'Reprisal':
        return "Accused's Reprisal is:";
      case 'Sex':
        return "Accused's Sex is:";
      default:
        return "Accused's basis is:";
    }
  };

  const getBasisFieldName = (prefix: 'your' | 'accused') => {
    switch (discriminationBasis) {
      case 'Age':
        return prefix === 'your' ? 'your_age' : 'accused_age';
      case 'Color':
        return prefix === 'your' ? 'your_color' : 'accused_color';
      case 'Mental/Physical Handicap':
        return prefix === 'your' ? 'your_handicap' : 'accused_handicap';
      case 'National Origin':
        return prefix === 'your' ? 'your_national_origin' : 'accused_national_origin';
      case 'Race':
        return prefix === 'your' ? 'your_race' : 'accused_race';
      case 'Religion':
        return prefix === 'your' ? 'your_religion' : 'accused_religion';
      case 'Reprisal':
        return prefix === 'your' ? 'your_reprisal' : 'accused_reprisal';
      case 'Sex':
        return prefix === 'your' ? 'your_sex' : 'accused_sex';
      default:
        return null;
    }
  };

  const caseTypeOptions = [
    'Accommodation',
    'Different Treatment',
    'Harassment',
    'Nonselection',
    'Sexual Harassment',
    'Termination',
  ];

  const discriminationBasisOptions = [
    'Age',
    'Color',
    'Mental/Physical Handicap',
    'National Origin',
    'Race',
    'Religion',
    'Reprisal',
    'Sex',
  ];

  return (
    <Card
      title={
        <Space>
          <InfoCircleOutlined />
          <span>General Information</span>
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Case Number"
              name="case_no"
              initialValue={caseNo}
            >
              {loadingCaseNo ? (
                <Spin size="small" />
              ) : (
                <InputNumber
                  placeholder="Auto-generated"
                  disabled
                  value={caseNo || undefined}
                  style={{ width: '100%', backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  readOnly
                />
              )}
            </Form.Item>
          </Col>
        <Col span={8}>
          <Form.Item
            label="Case Type"
            name="case_type"
            rules={[{ required: true, message: 'Please select the case type!' }]}
          >
            <Select placeholder="Select case type" showSearch>
              {caseTypeOptions.map(option => (
                <Option key={option} value={option}>{option}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="Discrimination Basis"
            name="discrimination_basis"
            rules={[{ required: true, message: 'Please select the discrimination basis!' }]}
          >
            <Select placeholder="Select discrimination basis" showSearch>
              {discriminationBasisOptions.map(option => (
                <Option key={option} value={option}>{option}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Date Discrimination Occurred"
            name="date_discrimination_occurred"
          >
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Date of Interview"
            name="date_of_interview"
          >
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
        </Col>
      </Row>

      {/* Personal Information */}
      <Card
        type="inner"
        title="Personal Information"
        style={{ marginTop: 16 }}
      >
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Last Name"
              name="last_name"
            >
              <Input placeholder="Enter last name" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="First Name"
              name="first_name"
            >
              <Input placeholder="Enter first name" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Middle"
              name="middle"
            >
              <Input placeholder="Enter middle name" />
            </Form.Item>
          </Col>
        </Row>
        {discriminationBasis && getBasisFieldName('your') && (
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label={getYourBasisLabel()}
                name={getBasisFieldName('your')!}
              >
                <Input placeholder={`Enter ${discriminationBasis.toLowerCase()}`} />
              </Form.Item>
            </Col>
          </Row>
        )}
      </Card>

      {/* Accused's Information */}
      <Card
        type="inner"
        title="Accused's Information"
        style={{ marginTop: 16 }}
      >
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Company Name"
              name="company_name"
            >
              <Input placeholder="Enter company name" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Last Name"
              name="accused_last_name"
            >
              <Input placeholder="Enter accused's last name" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="First Name"
              name="accused_first_name"
            >
              <Input placeholder="Enter accused's first name" />
            </Form.Item>
          </Col>
        </Row>
        {discriminationBasis && getBasisFieldName('accused') && (
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label={getAccusedBasisLabel()}
                name={getBasisFieldName('accused')!}
              >
                <Input placeholder={`Enter accused's ${discriminationBasis.toLowerCase()}`} />
              </Form.Item>
            </Col>
          </Row>
        )}
      </Card>
    </Card>
  );
};

export default GeneralInformation;

